import { Children, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import Icon from '../../docs/Icon'
import { useReducedMotion } from '../../../state/useReducedMotion'

export type CarouselProps = {
  /** Names the carousel for assistive technology, for example "Program highlights". */
  label: string
  /** One element per slide, usually a card. */
  children: ReactNode
}

/**
 * A sideways row of slides the page scrolls past. Slides move with the
 * Previous and Next buttons, a swipe or a trackpad, and snap into place. It
 * never advances on its own.
 */
export default function Carousel({ label, children }: CarouselProps) {
  const id = useId()
  const viewport = useRef<HTMLDivElement>(null)
  const back = useRef<HTMLButtonElement>(null)
  const next = useRef<HTMLButtonElement>(null)
  const reduced = useReducedMotion()
  const [edges, setEdges] = useState({ start: true, end: false })
  const slides = Children.toArray(children)

  useEffect(() => {
    const element = viewport.current
    if (!element) return
    const update = () =>
      setEdges({ start: element.scrollLeft <= 1, end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 1 })
    update()
    element.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      element.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [slides.length])

  // A button hides at its end; focus there moves to the other button.
  useEffect(() => {
    if (edges.start && document.activeElement === back.current) next.current?.focus()
    if (edges.end && document.activeElement === next.current) back.current?.focus()
  }, [edges])

  // One slide at a time: the width of a slide and the gap after it.
  const step = (direction: 1 | -1) => {
    const element = viewport.current
    const slide = element?.querySelector<HTMLElement>('.mds-carousel__slide')
    if (!element || !slide) return
    const gap = parseFloat(getComputedStyle(slide.parentElement!).columnGap) || 0
    element.scrollBy({ left: direction * (slide.offsetWidth + gap), behavior: reduced ? 'instant' : 'smooth' })
  }

  return (
    <section className="mds-carousel" aria-roledescription="carousel" aria-label={label}>
      <button
        ref={back}
        type="button"
        className="mds-carousel__button mds-carousel__button--back"
        aria-label="Previous slide"
        aria-controls={id}
        hidden={edges.start}
        onClick={() => step(-1)}
      >
        <Icon name="chevron" size={24} />
      </button>
      <button
        ref={next}
        type="button"
        className="mds-carousel__button"
        aria-label="Next slide"
        aria-controls={id}
        hidden={edges.end}
        onClick={() => step(1)}
      >
        <Icon name="chevron" size={24} />
      </button>
      <div ref={viewport} className="mds-carousel__viewport">
        <div id={id} className="mds-carousel__track">
          {slides.map((slide, i) => (
            <div
              key={i}
              className="mds-carousel__slide"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${slides.length}`}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
